import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, getDocs, Timestamp, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useToast } from '../../../components/ui/use-toast';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';

interface Voucher {
  id: string;
  code: string;
  value: number;
  startDate: Date;
  endDate: Date;
  multipleUse: boolean;
  createdAt: Date;
}

export default function VouchersPage() {
  const [code, setCode] = useState('');
  const [value, setValue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [multipleUse, setMultipleUse] = useState(false);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadVouchers();
  }, []);

  const loadVouchers = async () => {
    try {
      const vouchersRef = collection(db, 'vouchers');
      const q = query(vouchersRef);
      const querySnapshot = await getDocs(q);
      
      const loadedVouchers: Voucher[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        loadedVouchers.push({
          id: doc.id,
          code: data.code,
          value: data.value,
          startDate: data.startDate.toDate(),
          endDate: data.endDate.toDate(),
          multipleUse: data.multipleUse,
          createdAt: data.createdAt.toDate(),
        });
      });

      setVouchers(loadedVouchers.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
    } catch (error) {
      console.error('Error loading vouchers:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os vouchers.',
        variant: 'destructive',
      });
    }
  };

  const generateRandomCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setCode(result);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code || !value || !startDate || !endDate) {
      toast({
        title: 'Erro',
        description: 'Por favor, preencha todos os campos.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const voucherData = {
        code,
        value: parseFloat(value),
        startDate: Timestamp.fromDate(new Date(startDate)),
        endDate: Timestamp.fromDate(new Date(endDate)),
        multipleUse,
        createdAt: Timestamp.fromDate(new Date()),
      };

      await addDoc(collection(db, 'vouchers'), voucherData);
      
      toast({
        title: 'Sucesso',
        description: 'Voucher criado com sucesso!',
      });

      // Reset form
      setCode('');
      setValue('');
      setStartDate('');
      setEndDate('');
      setMultipleUse(false);
      
      // Reload vouchers
      await loadVouchers();
    } catch (error) {
      console.error('Error creating voucher:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível criar o voucher.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (voucherId: string) => {
    try {
      setDeletingId(voucherId);
      await deleteDoc(doc(db, 'vouchers', voucherId));
      
      toast({
        title: 'Sucesso',
        description: 'Voucher eliminado com sucesso!',
      });
      
      // Reload vouchers
      await loadVouchers();
    } catch (error) {
      console.error('Error deleting voucher:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível eliminar o voucher.',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Gestão de Vouchers</h1>
      
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Criar Novo Voucher</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Código</Label>
                <div className="flex gap-2">
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="Digite o código ou gere um aleatório"
                  />
                  <Button type="button" variant="outline" onClick={generateRandomCode}>
                    Gerar
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="value">Valor (€)</Label>
                <Input
                  id="value"
                  type="number"
                  step="0.01"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="startDate">Data de Início</Label>
                <Input
                  id="startDate"
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">Data de Fim</Label>
                <Input
                  id="endDate"
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="multipleUse"
                  checked={multipleUse}
                  onCheckedChange={setMultipleUse}
                />
                <Label htmlFor="multipleUse">Permitir múltiplos usos por cliente</Label>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'A criar...' : 'Criar Voucher'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Vouchers Existentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Validade</TableHead>
                    <TableHead>Múltiplos Usos</TableHead>
                    <TableHead className="w-[100px]">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vouchers.map((voucher) => (
                    <TableRow key={voucher.id}>
                      <TableCell className="font-medium">{voucher.code}</TableCell>
                      <TableCell>{voucher.value.toFixed(2)}€</TableCell>
                      <TableCell>
                        {new Date(voucher.startDate).toLocaleDateString()} até{' '}
                        {new Date(voucher.endDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{voucher.multipleUse ? 'Sim' : 'Não'}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(voucher.id)}
                          disabled={deletingId === voucher.id}
                          className="hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
